import React from "react";
import {
  GridStyle,
  designTabBoxProps,
  greyedOutFields,
} from "../../../../../../styles/muiProps";
import { CustomSelect } from "../../../../../../components/widgets/CustomSelect";
import CustomTextField from "../../../../../../components/widgets/CustomTextField";
import { Box, Button, Grid, InputAdornment, Typography } from "@mui/material";
import { materialData } from "./FieldsData/DesignTabFields";
import { useFormContext } from "react-hook-form";
import { getSingleMaterialData } from "../sqliteApi";
import { materialData as fetchLocalMaterialData } from "../../API`s/pv/sqliteApi";
import { toLegacy, fromLegacyKey, toLegacyKey } from "../../fieldTranslation";

const MaterialsGrid = () => {
  const { getValues, setValue } = useFormContext();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const fetchMaterialData = async () => {
    const data = getValues();
    setLoading(true);
    setError(null);
    try {
      // Primary path: query the server-hosted pv tables directly via the
      // generic /sqlite-db/query endpoint (same tables the exe reads from
      // its local pv.sqlite, same normalized REPLACE() matching + "" clear
      // on miss). This bypasses POST /pressure_vessel/materialData which
      // currently returns 500.
      // Server engine still uses legacy WinForms names (ComboBox9/TextBox68…),
      // so translate clean UI keys before the lookup (same as exe bridge).
      let materials = null;
      const localRes = await fetchLocalMaterialData({ ...toLegacy(data) });
      if (localRes?.error) {
        console.warn(
          "Direct pv table lookup failed, falling back to /materialData:",
          localRes.error,
        );
      } else if (localRes?.data) {
        materials = localRes.data;
      }

      // Fallback: dedicated server handler (currently 500s, kept while the
      // server team fixes it).
      if (!materials) {
        const body = await getSingleMaterialData(toLegacy(data));
        materials = body?.data ?? body ?? {};
      }
      if (materials?.error) {
        throw new Error(materials.error);
      }

      // Patch only stress/curve fields instead of reset() so the rest of
      // the design/shell/nozzle form is preserved.
      materialData.forEach((elem) => {
        if (!elem.stressFieldName || !elem.curveFieldName) return;
        const hasMaterial = Boolean(data?.[elem.materialFieldName]);
        if (!hasMaterial) return;
        const stress =
          materials[elem.stressFieldName] ??
          materials[toLegacyKey(elem.stressFieldName)] ??
          "";
        const curve =
          materials[elem.curveFieldName] ??
          materials[toLegacyKey(elem.curveFieldName)] ??
          "";
        setValue(elem.stressFieldName, stress, { shouldDirty: true });
        setValue(elem.curveFieldName, curve, { shouldDirty: true });
      });

      // Fan-out any legacy stress pair fields the server returned (e.g.
      // shell txt_AS_CSC/txt_AS_EHC, nozzle txt_NAS_NC1). Only these
      // material-related keys are patched — the rest of the form is left
      // untouched (unlike the old reset(res.data)).
      const allowedPairKeys = new Set(
        materialData.flatMap((elem) => [
          elem.stressFieldName,
          elem.curveFieldName,
          toLegacyKey(elem.stressFieldName),
          toLegacyKey(elem.curveFieldName),
        ]),
      );
      Object.entries(materials).forEach(([key, value]) => {
        if (typeof value === "object" || value === undefined) return;
        const cleanKey = fromLegacyKey(key);
        const isAllowed =
          allowedPairKeys.has(key) ||
          allowedPairKeys.has(cleanKey) ||
          key.startsWith("txt_AS") ||
          key.startsWith("txt_SAS") ||
          key.startsWith("txt_NAS");
        const alreadyPatched = materialData.some(
          (elem) =>
            elem.stressFieldName === cleanKey ||
            elem.curveFieldName === cleanKey,
        );
        if (isAllowed && !alreadyPatched) {
          setValue(cleanKey, value, { shouldDirty: true });
        }
      });
    } catch (err) {
      console.error("Failed to fetch material data:", err);
      const serverDetail =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === "string"
          ? err.response.data
          : null);
      setError(
        (serverDetail ? `${serverDetail} ` : "") +
          (err?.message || "Failed to fetch material data"),
      );
    } finally {
      setLoading(false);
    }
  };
  // xs={2} sm={2.6} md={3} lg={3} xl={2.6}
  //  xs={2} sm={2.6} md={3} lg={3} xl={2.6}
  return (
    // <Grid item xs={3} sm={4} md={6} lg={5} xl={4} >
    <Grid item xs={4} sm={5.2} md={6} lg={6} xl={5.2}>
      <Box {...designTabBoxProps} noValidate>
        <Grid
          id="inner-container"
          item
          container
          columnSpacing={0.5}
          // columns={24}
        >
          <Grid item sx={{ ...GridStyle }} xs={3}>
            <Typography></Typography>
          </Grid>

          {/* Column 1 */}
          <Grid item sx={{ ...GridStyle }} xs={3}>
            <Typography fontSize={"0.75rem"} textAlign={"center"}>
              Material
            </Typography>
          </Grid>

          {/* Column 2 */}
          <Grid item sx={{ ...GridStyle }} xs={1.5}>
            <Typography fontSize={"0.75rem"} textAlign={"center"}>
              Stress (N/mm^2)
            </Typography>
          </Grid>

          {/* Column 3 */}
          <Grid item sx={{ ...GridStyle }} xs={1.5}>
            <Typography fontSize={"0.75rem"} textAlign={"center"}>
              Curve
            </Typography>
          </Grid>

          {/* Column 3 */}
          <Grid item sx={{ ...GridStyle }}>
            <Typography fontSize={"0.75rem"} textAlign={"center"}>
              Requirement
            </Typography>
          </Grid>

          {materialData.map((elem) =>
            elem.label !== "Gasket" ? (
              <Grid
                id="inner-container2"
                item
                key={elem.label}
                container
                columns={12}
                columnSpacing={0.5}
              >
                <Grid item sx={{ ...GridStyle }} xs={3}>
                  <Typography fontSize={"0.75rem"}>{elem.label}</Typography>
                </Grid>

                {/* TXT FIELD 1 */}
                <Grid item sx={{ ...GridStyle }} xs={3}>
                  <CustomSelect
                    label=""
                    name={elem.materialFieldName}
                    required
                    pairField={elem.pairField ?? false}
                    menuItems={elem.select}
                  />
                </Grid>

                {/* TXT FIELD 2 */}
                <Grid item sx={{ ...GridStyle }} xs={1.5}>
                  <CustomTextField
                    label=""
                    id={elem.stressFieldName}
                    name={elem.stressFieldName}
                    type={elem.type}
                    required
                    disabled
                    inputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {elem.endAdornment}
                        </InputAdornment>
                      ),
                      style: greyedOutFields,
                    }}
                  />
                </Grid>

                {/* TXT FIELD 3 */}
                <Grid item sx={{ ...GridStyle }} xs={1.5}>
                  <CustomTextField
                    label=""
                    id={elem.curveFieldName}
                    name={elem.curveFieldName}
                    type={elem.type}
                    required
                    disabled
                    inputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {elem.endAdornment}
                        </InputAdornment>
                      ),
                      style: greyedOutFields,
                    }}
                  />
                </Grid>

                <Grid item sx={{ ...GridStyle }} xs={3}>
                  <CustomSelect
                    label=""
                    name={elem.requirementFieldName}
                    required
                    pairField={false}
                    menuItems={elem.requirementItems ?? []}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid
                id="inner-container2"
                item
                container
                columns={12}
                columnSpacing={0.5}
              >
                <Grid item sx={{ ...GridStyle }} xs={3}>
                  <Typography fontSize={"0.75rem"}>{elem.label}</Typography>
                </Grid>

                {/* TXT FIELD 1 */}
                <Grid item sx={{ ...GridStyle, paddingRight: "0px" }} xs={6}>
                  <CustomSelect
                    label=""
                    name={elem.materialFieldName}
                    required
                    pairField={elem.pairField ?? false}
                    menuItems={elem.select}
                  />
                </Grid>

                <Grid item sx={{ ...GridStyle }} xs={3}>
                  <CustomSelect
                    label=""
                    name={elem.requirementFieldName}
                    required
                    pairField={false}
                    menuItems={elem.requirementItems ?? []}
                  />
                </Grid>
              </Grid>
            )
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap :1}}>
          <Button
            variant="contained"
            onClick={fetchMaterialData}
            disabled={loading}
          >
            {loading ? "Loading..." : "Get Material Data"}
          </Button>
          {error ? (
            <Typography fontSize={"0.75rem"} color="error">
              {error}
            </Typography>
          ) : null}
          </Box>
        </Grid>
      </Box>
    </Grid>
  );
};

export default MaterialsGrid;
